import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface HeroSectionProps {
  heroImage?: string;
}

const HeroWrapper = styled.section`
  position: relative;
  padding: clamp(120px, 18vw, 200px) 0 clamp(80px, 12vw, 140px);
  background: radial-gradient(circle at 15% 20%, rgba(94, 107, 96, 0.08), transparent 55%),
    radial-gradient(circle at 80% 0%, rgba(209, 179, 150, 0.12), transparent 60%),
    linear-gradient(160deg, #fdfaf6 0%, #f5efe9 45%, #f7f3ee 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 96px clamp(18px, 6vw, 28px) 72px;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(40px, 7vw, 80px);
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    gap: clamp(28px, 8vw, 44px);
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
  }
`;

const HeroContent = styled.div`
  display: grid;
  gap: clamp(18px, 3.5vw, 32px);

  @media (max-width: 768px) {
    text-align: center;
    justify-items: center;
    width: 100%;
    padding: 0 clamp(16px, 6vw, 26px);
  }
`;

const HeroOverline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(47, 58, 52, 0.1);
  color: rgba(47, 58, 52, 0.88);
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.8rem, 4vw, 4.2rem);
  font-weight: 700;
  color: #2c2a29;
  line-height: 1.05;
  letter-spacing: -0.02em;
`;

const HeroDescription = styled.p`
  font-size: clamp(1.1rem, 2.2vw, 1.35rem);
  line-height: 1.8;
  color: rgba(44, 42, 41, 0.75);
  max-width: 540px;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px 32px;
  border-radius: 999px;
  background: linear-gradient(135deg, #2f3a34 0%, #1f2823 100%);
  color: #fff;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 38px rgba(26, 35, 30, 0.25);
  }

  @media (max-width: 480px) {
    flex: 1 1 100%;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 30px;
  border-radius: 999px;
  border: 1px solid rgba(47, 58, 52, 0.3);
  color: #2f3a34;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(47, 58, 52, 0.06);
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    flex: 1 1 100%;
  }
`;

const HeroHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const HighlightCard = styled.div`
  padding: 18px 20px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(227, 221, 213, 0.6);
  display: grid;
  gap: 6px;
  box-shadow: 0 12px 28px rgba(37, 45, 40, 0.08);
`;

const HighlightTitle = styled.span`
  font-weight: 600;
  color: #2f3a34;
  font-size: 0.95rem;
`;

const HighlightDescription = styled.span`
  color: rgba(47, 58, 52, 0.7);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const HeroVisual = styled.div<{ image?: string }>`
  position: relative;
  border-radius: clamp(24px, 4vw, 40px);
  overflow: hidden;
  min-height: clamp(280px, 32vw, 440px);
  background: linear-gradient(145deg, rgba(47, 58, 52, 0.65), rgba(102, 87, 73, 0.35));
  box-shadow: 0 35px 60px rgba(38, 45, 40, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 540px;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: clamp(320px, 90vw, 420px);
    min-height: clamp(260px, 70vw, 360px);
    padding: clamp(18px, 6vw, 28px);
    flex-direction: column;
    gap: clamp(16px, 5vw, 24px);
  }

  @media (max-width: 480px) {
    max-width: clamp(240px, 92vw, 340px);
    border-radius: 28px;
  }
`;

const HeroImage = styled.img`
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: contain;
  z-index: 1;
  max-width: 100%;

  @media (max-width: 768px) {
    width: min(100%, 320px);
    height: auto;
  }
`;

const VisualBadge = styled.div`
  position: absolute;
  top: 32px;
  left: 32px;
  padding: 16px 20px;
  border-radius: 16px;
  background: rgba(247, 243, 238, 0.88);
  backdrop-filter: blur(6px);
  color: #2c2a29;
  display: grid;
  gap: 4px;
  width: clamp(180px, 45%, 220px);
  box-shadow: 0 20px 35px rgba(38, 45, 40, 0.18);
  z-index: 3;

  span:first-child {
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(44, 42, 41, 0.68);
  }

  span:last-child {
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1.4;
  }

  @media (max-width: 768px) {
    position: static;
    top: auto;
    left: auto;
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
    padding: clamp(14px, 4.5vw, 20px);
    text-align: center;
    box-shadow: 0 16px 32px rgba(38, 45, 40, 0.16);
    backdrop-filter: blur(8px);
    word-break: break-word;
  }
`;

const highlightItems = [
  {
    title: 'Florister i Kramfors',
    description: 'Skapar handbundna buketter och inredningsdetaljer med omsorg varje vecka.'
  },
  {
    title: 'Leverans & upphämtning',
    description: 'Flexibla leveranser och butikshämtning – vi hjälper dig hitta den bästa lösningen.'
  },
  {
    title: 'Curerat sortiment',
    description: 'Utvalt från nordiska designers och hållbara varumärken.'
  }
];

const HeroSection: React.FC<HeroSectionProps> = ({ heroImage }) => {

  return (
    <HeroWrapper>
      <div className="container">
        <HeroInner>
          <HeroContent>
            <HeroOverline>Inredning & floristik i perfekt harmoni</HeroOverline>
            <HeroTitle>
              Blomsterbutik & inredningsdetaljer i Kramfors
            </HeroTitle>
            <HeroDescription>
              Lilla Flora är en mysig blomsterbutik i Kramfors med handplockade inredningsdetaljer,
              ljus &amp; doftljus och omtänksamma presenter. Här hittar du allt från vardagsbuketter
              till gå bort‑presenter och detaljer som gör hemmet mjukare och mer personligt – både i
              butiken och i webshopen.
            </HeroDescription>
            <HeroActions>
              <PrimaryButton to="/produkter">
                Utforska webbutiken
              </PrimaryButton>
              <SecondaryButton to="/kontakt">
                Besök butiken i Kramfors
              </SecondaryButton>
            </HeroActions>
            <HeroHighlights>
              <HighlightCard>
                <HighlightTitle>Ljus &amp; doftljus för hemmamys</HighlightTitle>
                <HighlightDescription>
                  Utforska vårt sortiment av ljus, doftljus och lyktor för vardagsmys och fest – direkt i{" "}
                  <Link to="/produkter?kategori=doftljus-och-doftpinnar">kategorin ljus &amp; doft</Link>.
                </HighlightDescription>
              </HighlightCard>
              <HighlightCard>
                <HighlightTitle>Gå bort‑presenter &amp; dukning</HighlightTitle>
                <HighlightDescription>
                  Hitta omtänksamma <Link to="/produkter?kategori=dukning-och-servering">gå bort‑presenter</Link> och
                  dukningsdetaljer som gör middagen extra speciell.
                </HighlightDescription>
              </HighlightCard>
              <HighlightCard>
                <HighlightTitle>Mjuka textilier &amp; hemkänsla</HighlightTitle>
                <HighlightDescription>
                  Upptäck <Link to="/produkter?kategori=textiler">textilier</Link> som kuddar, plädar och kökstextilier
                  som adderar värme till varje rum.
                </HighlightDescription>
              </HighlightCard>
            </HeroHighlights>
          </HeroContent>

          <HeroVisual>
            {heroImage && <HeroImage src={heroImage} alt="Butiken Lilla Flora" />}
            <VisualBadge>
              <span>Välkommen in</span>
              <span>
                Kliv in i vår mysiga butik i Kramfors – fylld av blomster, dofter och handplockad inredning.
              </span>
            </VisualBadge>
          </HeroVisual>
        </HeroInner>
      </div>
    </HeroWrapper>
  );
};

export default HeroSection;
